import { useEffect, useRef, useState } from 'react';

/**
 * A note's images at full size, over the page.
 *
 * Built on <dialog> with `showModal()` rather than a positioned div, because the browser then
 * supplies what a hand-rolled overlay gets wrong: Escape closes it, focus is trapped inside
 * and returned to the thumbnail afterwards, the page behind is inert to screen readers, and
 * `::backdrop` dims it.
 *
 * The parent mounts this only while it is open, and unmounts it on `onClose`.
 */
export default function Lightbox({
  images,
  start,
  onClose,
}: {
  images: string[];
  start: number;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const [index, setIndex] = useState(start);
  const many = images.length > 1;

  // No cleanup that calls close(). Unmounting removes the dialog, which ends the modal state
  // by itself — and close() fires the `close` event, so under StrictMode's mount, unmount,
  // remount the lightbox would dismiss itself the moment it opened.
  useEffect(() => {
    const dialog = ref.current;
    if (dialog && !dialog.open) dialog.showModal();
  }, []);

  // Wraps at both ends: with a handful of photos, stopping dead at the last one reads as
  // the control breaking.
  const step = (by: number) => setIndex((i) => (i + by + images.length) % images.length);

  const src = images[index];
  if (!src) return null;

  return (
    <dialog
      ref={ref}
      className="lightbox"
      aria-label={many ? `Image ${index + 1} of ${images.length}` : 'Image'}
      // Escape fires `close` natively; this keeps the parent's state in step with it.
      onClose={onClose}
      // A click that lands on the dialog itself, not the picture or a control, is a click
      // on the dimmed area around it.
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onKeyDown={(e) => {
        if (!many) return;
        if (e.key === 'ArrowRight') step(1);
        if (e.key === 'ArrowLeft') step(-1);
      }}
    >
      <img src={src} alt="" />

      <button type="button" className="lightbox-close" aria-label="Close" onClick={onClose}>
        ×
      </button>

      {many && (
        <>
          <button
            type="button"
            className="lightbox-nav prev"
            aria-label="Previous image"
            onClick={() => step(-1)}
          >
            ‹
          </button>
          <button
            type="button"
            className="lightbox-nav next"
            aria-label="Next image"
            onClick={() => step(1)}
          >
            ›
          </button>
          <span className="lightbox-count">
            {index + 1} / {images.length}
          </span>
        </>
      )}
    </dialog>
  );
}
